import React, { useState } from 'react';
import { useRecordContext, useRefresh, useNotify } from 'react-admin';
import VerifiedIcon from '@mui/icons-material/Verified';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';

import IconButton from '../../common/buttons/IconButton';
import { validateInvoiceUBL } from '../../../services/api';

const ValidatePeppolButton = () => {
    const record = useRecordContext();
    const refresh = useRefresh();
    const notify = useNotify();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleValidate = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await validateInvoiceUBL(record?.id);
            setResult(data);

            // Check for PDF/UBL mismatch first
            if (!data.success && data.message === 'PDF and UBL are not the same') {
                setError('CRITICAL: PDF and UBL calculations do not match. This must be fixed before PEPPOL validation.');
                notify('PDF and UBL mismatch - validation aborted', { type: 'error' });
            }
            // Check for error in validationResult
            else if (data.validationResult?.error) {
                setError(`Validation error: ${data.validationResult.error}`);
                notify('PEPPOL validation error', { type: 'error' });
            } else if (!data.success && data.message) {
                setError(data.message);
                notify('PEPPOL validation error', { type: 'error' });
            } else if (data.valid) {
                notify('PEPPOL validation passed ✓');
            } else {
                notify(`PEPPOL validation failed: ${data.errors?.length || 0} errors`, { type: 'warning' });
            }

            refresh();
        } catch (err) {
            setError(err.message || 'Error validating invoice');
            notify('Error validating PEPPOL', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = async () => {
        setOpen(true);
        await handleValidate();
    };

    const handleClose = () => {
        setOpen(false);
        setResult(null);
        setError(null);
    };

    const isValid = result?.valid === true;
    const errors = result?.errors || [];

    return (
        <>
            <IconButton icon={<VerifiedIcon />} onClick={handleOpen}>
                Validate PEPPOL
            </IconButton>

            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>PEPPOL Validation Results</DialogTitle>
                <DialogContent sx={{ minHeight: '300px' }}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error">{error}</Alert>
                    ) : result ? (
                        <Box>
                            {result.message === 'PDF and UBL are not the same' ? (
                                <>
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        <strong>CRITICAL ERROR: PDF and UBL Mismatch</strong>
                                        <br />
                                        The PDF and UBL calculations do not match. This must be fixed before PEPPOL validation can proceed.
                                    </Alert>
                                    <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        {errors.map((err, idx) => (
                                            <Card
                                                key={idx}
                                                sx={{
                                                    mb: 2,
                                                    backgroundColor: '#ffebee',
                                                    borderLeft: '4px solid #d32f2f'
                                                }}
                                            >
                                                <CardContent>
                                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                                        <Chip
                                                            label={err.id}
                                                            size="small"
                                                            color="error"
                                                        />
                                                        <Chip
                                                            label={err.level}
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                        />
                                                    </Box>
                                                    <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                                                        {err.description}
                                                    </Typography>
                                                    {err.details && (
                                                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #ddd' }}>
                                                            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                                                                <strong>Details:</strong>
                                                            </Typography>
                                                            {err.details.map((detail, detailIdx) => (
                                                                <Typography key={detailIdx} variant="caption" sx={{ display: 'block', color: '#999' }}>
                                                                    {detail.type}: PDF={detail.pdf?.toFixed ? detail.pdf.toFixed(2) : detail.pdf}, UBL={detail.ubl?.toFixed ? detail.ubl.toFixed(2) : detail.ubl}
                                                                </Typography>
                                                            ))}
                                                        </Box>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Box>
                                </>
                            ) : isValid ? (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    Invoice is PEPPOL compliant ✓
                                </Alert>
                            ) : (
                                <>
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        Invoice has {errors.length} validation error(s)
                                    </Alert>
                                    <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        {errors.map((err, idx) => (
                                            <Card
                                                key={idx}
                                                sx={{
                                                    mb: 2,
                                                    backgroundColor:
                                                        err.level === 'FATAL_ERROR'
                                                            ? '#ffebee'
                                                            : '#fff3e0',
                                                    borderLeft:
                                                        err.level === 'FATAL_ERROR'
                                                            ? '4px solid #d32f2f'
                                                            : '4px solid #f57c00'
                                                }}
                                            >
                                                <CardContent>
                                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                                        <Chip
                                                            label={err.id}
                                                            size="small"
                                                            color={err.level === 'FATAL_ERROR' ? 'error' : 'warning'}
                                                        />
                                                        <Chip
                                                            label={err.level}
                                                            size="small"
                                                            variant="outlined"
                                                            color={err.level === 'FATAL_ERROR' ? 'error' : 'warning'}
                                                        />
                                                    </Box>
                                                    <Typography variant="body2" sx={{ color: '#666' }}>
                                                        {err.description}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Box>
                                </>
                            )}
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ValidatePeppolButton;
